/**
 * Access Features: one per authorization DOMAIN (not per controller). Several
 * controllers can share one - the internal `/tags` controller and the public `/v2`
 * tags controller are both `tags`.
 *
 * Granularity rule: every `(Feature, Op)` pair has exactly ONE minimum role. A domain
 * whose routes mix floors for the same op is split into two Features (e.g.
 * `dwd-workspace` / `dwd-workspace-admin`), never given a per-route exception.
 *
 * Browser-safe: pure constants, no runtime imports.
 * See docs/plans/2026-09-29-two-guard-access-model.md.
 */
export const Feature = {
  AI_CONTENT: 'ai-content',
  API_KEYS: 'api-keys',
  AUDIT: 'audit',
  AUTH_SESSION: 'auth-session',
  BILLING: 'billing',
  /** Public `/v2` billing reads (ADMIN), deliberately stricter than the in-app `billing` reads. */
  BILLING_API: 'billing-api',
  /** The one billing GET that is OWNER-only; every other billing read is VIEWER. */
  BILLING_UPGRADE_PREVIEW: 'billing-upgrade-preview',
  /** DWD workspace READS (list, sync preview/status, connected emails) - every role. */
  DWD_WORKSPACE: 'dwd-workspace',
  /** Every DWD write/consent/delete route, in-app and `/v2` provider-workspaces (ADMIN). */
  DWD_WORKSPACE_ADMIN: 'dwd-workspace-admin',
  /** Connect-flow / credential reads (OAuth authorize URLs, SMTP config) - EDITOR, unlike other account reads. */
  EMAIL_ACCOUNT_CONNECTION: 'email-account-connection',
  EMAIL_ACCOUNTS: 'email-accounts',
  /** `POST /email-accounts/bulk-connect` (ADMIN); the import-status read stays under `email-accounts`. */
  EMAIL_ACCOUNTS_BULK_CONNECT: 'email-accounts-bulk-connect',
  NOTIFICATIONS: 'notifications',
  ONBOARDING: 'onboarding',
  ORGANIZATION: 'organization',
  ORGANIZATION_MEMBERS: 'organization-members',
  ORGANIZATION_USAGE: 'organization-usage',
  /** Org-level `/v2/workspaces` API-key routes (list/create workspaces, quotas) - OWNER. */
  ORG_WORKSPACES: 'org-workspaces',
  /**
   * `GET /v2/workspaces` alone - VIEWER, so a workspace (`ti_ws_`) key, whose role `ApiKeyGuard` caps
   * at ADMIN, is still admitted (it lists just its own workspace). Every other route of that controller
   * stays OWNER under `org-workspaces`.
   */
  ORG_WORKSPACES_LIST: 'org-workspaces-list',
  PARTNER: 'partner',
  PREFERENCES: 'preferences',
  PROFILE: 'profile',
  RATE_LIMIT: 'rate-limit',
  REPORTS: 'reports',
  /** `/review-rewards`: the HighAdvocacy review prompt's eligibility read + impression write - VIEWER. */
  REVIEW_REWARDS: 'review-rewards',
  SALESHANDY: 'saleshandy',
  TAGS: 'tags',
  TEAM: 'team',
  TEAM_INVITE_LINKS: 'team-invite-links',
  /** `GET /settings/workspaces/:id/invites` (pending invites) - ADMIN; the members list stays VIEWER under `team`. */
  TEAM_INVITES: 'team-invites',
  /** `/template-collections`: reads VIEWER, writes ADMIN. Routes are commented out today (planned). */
  TEMPLATE: 'template',
  /** Public `/v2/workspaces/:id`: get VIEWER, patch OWNER (rename); the in-app workspace CRUD is `workspaces` (also OWNER on writes). */
  WORKSPACE_DETAILS: 'workspace-details',
  WORKSPACE_QUOTA: 'workspace-quota',
  WORKSPACES: 'workspaces',
} as const;
export type Feature = (typeof Feature)[keyof typeof Feature];

/** The operation a route performs. Usually its HTTP verb; a small allow-list of routes declares otherwise. */
export const Op = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete',
} as const;
export type Op = (typeof Op)[keyof typeof Op];

export const OPS: readonly Op[] = [Op.GET, Op.POST, Op.PUT, Op.PATCH, Op.DELETE];
